import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Zap, Layers, Code2, Box, Film, Wand2, Quote, Star, Bot, Brain, Network, Microscope, Video, Megaphone } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useSiteContent, imgSrc } from "../lib/siteContent";
import { PROCESS } from "../data/services";

const ICON_MAP = {
  ppt: Layers, software: Code2, "3d": Box, animated: Zap,
  "ai-agents": Bot, "ai-avatar": Brain, workflow: Network, "medical-ai": Microscope,
  video: Film, "video-edit": Video, "marketing-ads": Megaphone, website: Wand2,
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: 0.05 * i, duration: 0.7, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Landing() {
  const { content } = useSiteContent();
  const [testimonials, setTestimonials] = useState([]);
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      try {
        const [t, s, p] = await Promise.all([
          api.get("/testimonials"),
          api.get("/services"),
          api.get("/portfolio"),
        ]);
        setTestimonials(t.data);
        setServices(s.data);
        setPortfolio(p.data);
      } catch (e) {
        // optional content; page still renders
      }
    })();
  }, []);

  const hero = content.hero;
  const founder = content.founder;
  const visibleStats = (content.stats || []).filter((s) => s.value && s.value.trim());
  const featured = services.slice(0, 8);

  return (
    <div className="min-h-screen bg-[#F8F8F6] text-[#0E0E1A] relative overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-44 pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={imgSrc(hero.image)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#06060C]/40 via-[#06060C]/30 to-[#06060C]" />
        </div>
        <div className="absolute -top-32 left-1/3 w-[600px] h-[600px] bg-[#8B2FE8]/12 blur-[130px] rounded-full animate-pulse-glow" />
        <div className="absolute -bottom-32 right-10 w-[500px] h-[500px] bg-[#2060EE]/12 blur-[130px] rounded-full" />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.4em] text-[#00D4FF] mb-6 flex items-center gap-2"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {hero.eyebrow}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.9, ease: "easeOut" }}
            className="font-display font-black text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl text-white"
          >
            {hero.heading_pre} <span className="text-gradient-cyan">{hero.heading_accent1}</span>,
            <br />
            {hero.heading_mid} <span className="text-gradient-violet">{hero.heading_accent2}</span> {hero.heading_post}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-8 max-w-2xl text-lg text-gray-200 leading-relaxed"
          >
            {hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <Link to="/quote" data-testid="hero-cta-quote" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full btn-primary text-sm font-semibold">
              {hero.cta_primary}
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link to="/portfolio" data-testid="hero-cta-portfolio" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm text-white border border-white/25 hover:border-[#00D4FF]/60 hover:bg-white/5 transition-all">
              {hero.cta_secondary}
            </Link>
          </motion.div>

          {/* metrics row — only shown if the owner has set numbers */}
          {visibleStats.length > 0 && (
            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true }}
              className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl"
            >
              {visibleStats.map((m, idx) => (
                <motion.div key={`${m.label}-${idx}`} variants={fadeUp} custom={m.label} className="glass-card rounded-xl p-5">
                  <div className="font-display font-bold text-3xl text-gradient-cyan">{m.value}</div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72] mt-1">{m.label}</div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* SERVICES */}
      {featured.length > 0 && (
        <section id="services" className="relative py-28">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex items-end justify-between flex-wrap gap-6 mb-16">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">{content.services_section.eyebrow}</p>
                <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tight max-w-2xl">
                  {content.services_section.title_pre} <span className="text-gradient-cyan">{content.services_section.title_accent}</span>.
                </h2>
              </div>
              <Link to="/services" className="btn-ghost px-5 py-2.5 rounded-full text-sm">View all {services.length} →</Link>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {featured.map((s, i) => {
                const Icon = ICON_MAP[s.id] || Layers;
                return (
                  <motion.div
                    key={s.id} variants={fadeUp} custom={i}
                    initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
                    whileHover={{ y: -8 }}
                    data-testid={`service-card-${s.id}`}
                    className="group glass-card neon-border rounded-2xl p-6 transition-all duration-500 cursor-default"
                  >
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-[#00D4FF]/15 to-[#8B2FE8]/15 grid place-items-center mb-5 border border-[#2060EE]/20">
                      <Icon className="w-5 h-5 text-[#00D4FF]" />
                    </div>
                    <h3 className="font-display font-bold text-xl">{s.name}</h3>
                    <p className="text-sm text-[#00D4FF]/80 font-mono mt-1">{s.short}</p>
                    <p className="text-sm text-[#5A5A72] mt-4 leading-relaxed line-clamp-3">{s.description}</p>
                    <div className="mt-5 flex flex-wrap gap-1.5">
                      {(s.deliverables || []).slice(0, 3).map((d) => (
                        <span key={d} className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full bg-white/5 border border-black/[0.06] text-[#5A5A72]">{d}</span>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-10 text-center">
              <Link to="/services" data-testid="services-view-all-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-primary text-sm font-semibold">
                {content.services_section.cta} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* PORTFOLIO BENTO — only when the owner has added real work */}
      {portfolio.length > 0 && (
        <section id="portfolio" className="relative py-28 border-t border-black/[0.06]">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">{content.portfolio_section.eyebrow}</p>
                <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tight">
                  {content.portfolio_section.title_pre} <span className="text-gradient-violet">{content.portfolio_section.title_accent}</span>
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolio.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.6 }}
                  whileHover={{ y: -4 }}
                  data-testid={`portfolio-${p.id}`}
                  className="relative group overflow-hidden rounded-2xl border border-black/[0.06] aspect-[4/5]"
                >
                  <img src={imgSrc(p.image)} alt={p.title} className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#06060C] via-[#06060C]/40 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    {p.category && <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00D4FF] mb-1">{p.category}</p>}
                    <h3 className="font-display font-bold text-2xl text-white">{p.title}</h3>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PROCESS */}
      <section className="relative py-28 border-t border-black/[0.06]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">/ process</p>
          <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tight max-w-3xl">
            From idea to launch in <span className="text-gradient-cyan">four moves</span>.
          </h2>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.map((p, i) => (
              <motion.div
                key={p.step}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.6 }}
                className="relative p-7 rounded-2xl bg-white border border-black/[0.06]"
              >
                <div className="font-mono text-[#00D4FF] text-xs tracking-[0.3em]">STEP / {p.step}</div>
                <h3 className="font-display font-bold text-2xl mt-3">{p.title}</h3>
                <p className="text-[#5A5A72] text-sm mt-3 leading-relaxed">{p.body}</p>
                <div className="absolute top-7 right-7 w-2 h-2 rounded-full bg-[#00D4FF] animate-pulse-glow glow-cyan" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — only approved real ratings */}
      {testimonials.length > 0 && (
        <section className="relative py-28 border-t border-black/[0.06]">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">/ from real clients</p>
            <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tight max-w-3xl">
              Verified <span className="text-gradient-cyan">5-star</span> deliveries.
            </h2>
            <div className="mt-16 grid md:grid-cols-3 gap-5">
              {testimonials.slice(0, 6).map((t, i) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.6 }}
                  data-testid={`testimonial-${t.id}`}
                  className="glass-card rounded-2xl p-7 relative"
                >
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.stars }).map((_, n) => (
                      <Star key={`star-avg-${n}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <Quote className="w-5 h-5 text-[#00D4FF]/60 mb-2" />
                  <p className="text-[#374151] leading-relaxed">{t.comment}</p>
                  <div className="mt-6 pt-5 border-t border-black/[0.06]">
                    <p className="font-display font-bold">{t.customer_name}</p>
                    {t.project_title && (
                      <p className="text-xs text-[#6B7280] font-mono uppercase tracking-wider mt-1">
                        {t.service_type ? `${t.service_type} · ` : ""}{t.project_title}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FOUNDER */}
      <section className="relative py-28 border-t border-black/[0.06]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 relative">
              <div className="absolute -top-10 -left-10 w-72 h-72 bg-cyan-500/15 blur-[100px] rounded-full" />
              <div className="absolute -bottom-10 -right-10 w-72 h-72 bg-violet-500/15 blur-[100px] rounded-full" />
              <div className="relative aspect-square rounded-3xl overflow-hidden border border-black/[0.06] glass-card grid place-items-center">
                <img src={imgSrc(founder.image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#06060C] via-transparent to-[#06060C]/30" />
                <div className="relative text-center px-8">
                  <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-tr from-[#00D4FF] to-[#8B2FE8] grid place-items-center glow-cyan font-display font-black text-4xl text-black">
                    {founder.initials}
                  </div>
                  <p className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF]">{founder.role}</p>
                  <h3 className="font-display font-black text-3xl mt-2 text-white">{founder.name}</h3>
                  <p className="text-gray-300 text-sm mt-2">{founder.short}</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">{founder.eyebrow}</p>
              <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tight">
                {founder.heading_pre} <span className="text-gradient-violet">{founder.heading_accent1}</span>{founder.heading_mid} <span className="text-gradient-cyan">{founder.heading_accent2}</span> {founder.heading_post}
              </h2>
              <p className="text-[#4B5563] mt-6 text-lg leading-relaxed max-w-2xl">{founder.bio}</p>
              <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
                <div className="glass-card rounded-xl p-4">
                  <p className="font-display font-bold text-2xl text-gradient-cyan">{founder.stat1_v}</p>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72] mt-1">{founder.stat1_l}</p>
                </div>
                <div className="glass-card rounded-xl p-4">
                  <p className="font-display font-bold text-2xl text-gradient-cyan">{founder.stat2_v}</p>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72] mt-1">{founder.stat2_l}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 border-t border-black/[0.06]">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="font-display font-black text-4xl lg:text-7xl tracking-tight"
          >
            {content.cta_section.title_pre} <span className="text-gradient-cyan">{content.cta_section.title_accent}</span> {content.cta_section.title_post}
          </motion.h2>
          <p className="text-[#5A5A72] mt-6 max-w-xl mx-auto">{content.cta_section.subtitle}</p>
          <Link to="/quote" data-testid="footer-cta-quote" className="mt-10 inline-flex items-center gap-2 px-7 py-4 rounded-full btn-primary text-sm font-semibold">
            {content.cta_section.button} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
